/**
 * 文件夹卡点 → 开通弹窗文案所需"纯数据"（进 store，必须可序列化）。
 * 覆盖三个来源（PRD §3.2/§3.4）：batch（AI 批量）、single（单个创建）、resident（常驻入口）。
 * 档名 / 各档配额（tab 小字）来自接口4 tiers（见 folderTiers），缺失回落硬编码。
 */
import { tierLabel, tierTabNotes, resolveDisplayCap } from './folderTiers'
import type { TierInfo } from './folderTiers'

/** 文件夹卡点来源种类 · 渲染器据此选标题/副标题模板 */
export type FolderPaywallKind = 'batch' | 'single' | 'resident'

/** 开通弹窗 tab 小字各档配额文案（emphasis 段，如「20个」「更多」） */
export interface TabNotesData {
  basic: string
  advanced: string
  super: string
  enterprise: string
}

export interface FolderPaywallModalData {
  /** 来源种类 */
  kind: FolderPaywallKind
  /** 非会员 */
  isNovip: boolean
  /** 会员档名（来自 tiers.label，缺失回落硬编码；非会员为空串） */
  tierName: string
  /** 当前档上限（接口4 顶层 cap；超级/企业级的 -1 已归一到超级档 cap） */
  cap: number
  /** 当前用量（resident 副标题用） */
  used: number
  /** 预计用量（batch=used+选中数，single=used+1；resident 不用，回落 used） */
  newTotal: number
  /** 是否已超额 used>cap（resident 标题用） */
  exceeded: boolean
  /** 各档 tab 小字配额文案（来自 tiers，缺失回落硬编码） */
  tabNotes: TabNotesData
}

function baseData(
  vipKind: number,
  used: number,
  cap: number,
  tiers?: TierInfo[]
) {
  // 超级 / 企业级 cap=-1（"无上限"哨兵）→ 展示归一到超级档 cap（resolveDisplayCap），
  // 否则副标题会显示「至多拥有 -1个」；且无上限不算超额，exceeded 恒 false（不弹"已达上限"标题）
  const isUnlimited = cap === -1
  const displayCap = resolveDisplayCap(cap, tiers)
  return {
    isNovip: vipKind === 0,
    tierName: tierLabel(vipKind),
    cap: displayCap,
    used,
    exceeded: !isUnlimited && used > displayCap,
    tabNotes: tierTabNotes(tiers)
  }
}

/** AI 批量卡点（PRD §3.4 AI 批量创建）· 预计用量 = used + 选中数 */
export function batchModalData(input: {
  vipKind: number
  used: number
  cap: number
  selectedCount: number
  tiers?: TierInfo[]
}): FolderPaywallModalData {
  const { vipKind, used, cap, selectedCount, tiers } = input
  return {
    kind: 'batch',
    ...baseData(vipKind, used, cap, tiers),
    newTotal: used + selectedCount
  }
}

/** 单个创建卡点（PRD §3.4 单个创建）· 预计用量 = used + 1 */
export function singleModalData(input: {
  vipKind: number
  used: number
  cap: number
  tiers?: TierInfo[]
}): FolderPaywallModalData {
  const { vipKind, used, cap, tiers } = input
  return {
    kind: 'single',
    ...baseData(vipKind, used, cap, tiers),
    newTotal: used + 1
  }
}

/** 常驻入口卡点（PRD §3.4 常驻入口）· 用 used / exceeded，不用 newTotal */
export function residentModalData(input: {
  vipKind: number
  used: number
  cap: number
  tiers?: TierInfo[]
}): FolderPaywallModalData {
  const { vipKind, used, cap, tiers } = input
  return {
    kind: 'resident',
    ...baseData(vipKind, used, cap, tiers),
    newTotal: used
  }
}
